"use client"

import React, { createContext, useCallback, useContext, useMemo, useState } from 'react'
import { useRouter } from 'next/navigation'
import { useMe } from '@/hooks/use-me'

export type SelectedClient = { id?: number; name: string; public_id?: string } | null

type Ctx = {
  selectedClient: SelectedClient
  setSelectedClient: (c: SelectedClient) => void
  openSelector: () => void
  closeSelector: () => void
  isOpen: boolean
}

const ClientSelectionCtx = createContext<Ctx | null>(null)

export function useClientSelection() {
  const ctx = useContext(ClientSelectionCtx)
  if (!ctx) throw new Error('useClientSelection must be used within ClientSelectionProvider')
  return ctx
}

export function ClientSelectionProvider({ children }: { children: React.ReactNode }) {
  const router = useRouter()
  const { data: me } = useMe()
  const [selectedClient, setSelectedClientState] = useState<SelectedClient>(() => {
    try {
      if (typeof window === 'undefined') return null
      const raw = localStorage.getItem('app.selected_client')
      if (raw) {
        const parsed = JSON.parse(raw)
        return parsed
      }
    } catch {}
    return null
  })
  const [isOpen, setIsOpen] = useState(false)

  const isClientUser = !!me && me.user_type === 'client' && !me.is_staff && !me.is_superuser

  const lockedSelectedClient: SelectedClient = useMemo(() => {
    if (!isClientUser) return null
    const c = me?.active_client
    if (!c?.id) return null
    return {
      id: c.id,
      name: c.name,
      public_id: c.public_id || undefined,
    }
  }, [isClientUser, me?.active_client])

  const effectiveSelectedClient = isClientUser ? lockedSelectedClient : selectedClient


  const setSelectedClient = useCallback((c: SelectedClient) => {
    if (isClientUser) {
      return
    }
    setSelectedClientState(c)
    try {
      if (c) localStorage.setItem('app.selected_client', JSON.stringify(c))
      else localStorage.removeItem('app.selected_client')
    } catch {}
  }, [isClientUser])

  const openSelector = useCallback(() => {
    if (isClientUser) {
      return
    }
    setIsOpen(true)
  }, [isClientUser])

  const closeSelector = useCallback(() => {
    setIsOpen(false)
  }, [])

  const value = useMemo(
    () => ({ selectedClient: effectiveSelectedClient, setSelectedClient, openSelector, closeSelector, isOpen }),
    [effectiveSelectedClient, setSelectedClient, openSelector, closeSelector, isOpen]
  )

  return <ClientSelectionCtx.Provider value={value}>{children}</ClientSelectionCtx.Provider>
}
